import React, { ReactElement } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceDetailsProps } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { KeyValue } from "models/utilTypes/keyValue";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { DetailsTableView, SimpleMessage, Tags } from "components/common";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoiceDetails: React.FC<InvoiceDetailsProps> = (props) => {
  const invoiceService = useInvoiceService();
  const { total, tax, currency, referencePersonName, paymentTerms, invoiceRows } = props;

  const { dimensions } = useSelector((store: AppState) => ({
    dimensions: store.common.dimensions.invoiceDimensions ?? [],
  }));

  const dimensionKeyValues = React.useMemo(() => {
    const invoiceDimensions = invoiceService.getRowDimensionData(invoiceRows);
    return dimensions
      .map((dimensionMapItem) => {
        return dimensionMapItem.value
          .map((dimension) => {
            const invoiceDimension = invoiceDimensions.find((i) => i.dimensionId === dimension.id);
            return invoiceDimension
              ? `${dimension.name}${
                  invoiceDimension.amount !== total
                    ? ` (${LocalizationHelperInstance.getFormattedCurrency(invoiceDimension.amount, currency, 2, 2)})`
                    : ""
                }`
              : undefined;
          })
          .filter((d) => d !== undefined);
      })
      .flatMap((d) => d)
      .filter((d) => d !== undefined);
  }, [invoiceRows]);

  const periodisationText = React.useMemo(() => {
    const periodisationValues = Array.from(
      new Set(
        (invoiceRows as any[]).map((product: InvoiceFormTypes.Product | WintSuperkollAPIModel.InvoiceRow): string => {
          if ("periodisationStartYear" in product) {
            if (product.periodisationStartDate) {
              return DateHelper.formatDateRange(
                DateHelper.parseDateOnly(product.periodisationStartDate as unknown as string),
                product.periodisationEndDate
                  ? DateHelper.parseDateOnly(product.periodisationEndDate as unknown as string)
                  : undefined
              );
            }
            return DateHelper.formatMonthRange(
              DateHelper.getDateFromYearMonth(
                (product.periodisationStartYear ?? 0) * 100 + (product.periodisationStartMonth ?? 0)
              ),
              DateHelper.getDateFromYearMonth(
                (product.periodisationEndYear ?? 0) * 100 + (product.periodisationEndMonth ?? 0)
              )
            );
          } else if (typeof product.periodisation === "object") {
            if (product.periodisation?.mode === "date") {
              return DateHelper.formatDateRange(product.periodisation?.range?.start, product.periodisation?.range?.end);
            }
            return DateHelper.formatMonthRange(product.periodisation?.range?.start, product.periodisation?.range?.end);
          } else {
            return "";
          }
        })
      )
    );

    return periodisationValues.filter((val) => !UtilsHelper.isEmpty(val)).join(", ");
  }, [invoiceRows]);

  const getDetailsList = () => {
    const detailsList: Array<KeyValue<undefined, string | ReactElement> | typeof ACTION_SEPARATOR | undefined> = [
      new KeyValue(
        undefined,
        (
          <SimpleMessage icon={IconDefinitionConstants.WCalculator}>
            <Grid container className={cssExports.highlightText}>
              {LocalizationHelperInstance.getFormattedCurrency(Math.round(total), currency)}
            </Grid>
            <Grid container>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.OfWhichTax, undefined, {
                tax: LocalizationHelperInstance.getFormattedCurrency(tax, currency, 2),
              })}
            </Grid>
          </SimpleMessage>
        )
      ),
      referencePersonName
        ? new KeyValue(
            undefined,
            (
              <SimpleMessage icon={IconDefinitionConstants.WUser}>
                <Grid className={cssExports.textBlack}>{referencePersonName}</Grid>
              </SimpleMessage>
            )
          )
        : undefined,
      new KeyValue(
        undefined,
        (
          <SimpleMessage icon={IconDefinitionConstants.WCalendarTime}>
            <Grid className={cssExports.textBlack}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.DaysOfPaymentTerms, undefined, {
                days: `${paymentTerms}`,
              })}
            </Grid>
          </SimpleMessage>
        )
      ),
      dimensionKeyValues.length ? ACTION_SEPARATOR : undefined,
      dimensionKeyValues.length
        ? new KeyValue(
            undefined,
            (
              <SimpleMessage icon={IconDefinitionConstants.WHashtag}>
                <Grid className={cssExports.textBlack}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
                </Grid>
              </SimpleMessage>
            )
          )
        : undefined,
      dimensionKeyValues.length ? new KeyValue(undefined, <Tags tags={dimensionKeyValues as string[]} />) : undefined,
      periodisationText.length ? ACTION_SEPARATOR : undefined,
      periodisationText.length
        ? new KeyValue(
            undefined,
            (
              <SimpleMessage icon={IconDefinitionConstants.WCalendarTime}>
                <Grid className={cssExports.textBlack}>
                  {`${LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Common.Accrual
                  )} ${periodisationText}`}
                </Grid>
              </SimpleMessage>
            )
          )
        : undefined,
    ];

    return detailsList;
  };

  return (
    <DetailsTableView
      title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
      detailsList={getDetailsList()}
    />
  );
};

export default InvoiceDetails;
